import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { INSTRUCTIONS } from 'src/app/pages/ai/services/instructions.constants';
import {
  GeminiSchemaType,
  ProxyAiResponseSchemaService,
} from 'src/app/pages/ai/services/proxy-ai-response-schema.service';
import { TechStackInstallDto } from 'src/portal/admin-buyerscaddy-api/models/tech-stack-install-dto';
import { InstallService } from 'src/portal/admin-buyerscaddy-api/services/install.service';
import { ProductDto } from 'src/portal/public-api/models/product-dto';
import { VendorService } from 'src/portal/public-api/services/vendor.service';
import { CompanyDto } from 'src/portal/search-api/models/company-dto';
import { CompanyPageRequestDto } from 'src/portal/search-api/models/company-page-request-dto';
import { GlobalSearchResultTypeEnum } from 'src/portal/search-api/models/global-search-result-type-enum';
import { CompanyService } from 'src/portal/search-api/services/company.service';
import { ProductSelectDialogComponent } from '../product-select-dialog/product-select-dialog.component';


export interface ImportProductCompaniesDialogData {
  data?: string;
  title?: string;
}

interface ImportRow {
  ProductName?: string;
  ProductVendorDomainName?: string;
  CompanyDomainName?: string;
  CompanyName?: string;
  Country?: string;
  Industry?: string;
  Probability?: string;
  Source?: string;
  dateFirstVerified?: string;
  dateLastVerified?: string;
  selectedCompany?: CompanyDto | null;
  isSelected?: boolean;
}

@Component({
  selector: 'import-product-companies',
  templateUrl: './import-product-companies.component.html',
  styleUrls: ['./import-product-companies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportProductCompaniesComponent implements OnInit {
  readonly displayedColumns: string[] = [
    'selected',
    'CompanyName',
    'Country',
    'Industry',
    'Probability',
    'Source',
    'dateFirstVerified',
    'dateLastVerified',
  ];

  loading = false;
  rows: ImportRow[] = [];
  productLabel: string | null = null;
  selectedCompanies: CompanyDto[] = [];
  selectedProducts: ProductDto[] = [];
  selectedProduct: ProductDto | null = null;
  productsVendorDomain: string | null = null;
  productsQueryName: string | null = null;
  importing = false;

  get canImport(): boolean {
    const products = this.getTargetProducts();
    return (
      !this.loading &&
      !this.importing &&
      products.length > 0 &&
      products.some((product) => !!product?.id) &&
      this.selectedCompanies.some((company) => !!company?.id)
    );
  }

  get selectedCompanyCount(): number {
    return this.selectedCompanies.length;
  }

  get missingCompanyCount(): number {
    return this.rows.filter((row) => !row.selectedCompany).length;
  }

  constructor(
    private readonly dialogRef: MatDialogRef<ImportProductCompaniesComponent>,
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: ImportProductCompaniesDialogData | null,
    private readonly dialog: MatDialog,
    private readonly companyService: CompanyService,
    private vendorService: VendorService,
    private readonly installService: InstallService,
    private proxyAiResponseSchemaService: ProxyAiResponseSchemaService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.productLabel = '';
    this.getData(this.dialogData.data);
  }

  async getData(source: unknown): Promise<void> {
    if (!source) {
      this.rows = [];
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();
    this.selectedProducts = [];
    this.selectedProduct = null;

    try {
      const promptPayload =
        typeof source === 'string' ? source : JSON.stringify(source);

      const result = await this.proxyAiResponseSchemaService.analyzePrompt(
        INSTRUCTIONS.systemInstructionGetProductsDomain,
        promptPayload,
        GeminiSchemaType.DomainNameCompaniesUsingProductResponseSchema,
        0,
        'gemini-flash-latest'
      );

      console.log(result);
      const items = Array.isArray(result?.items)
        ? result.items
        : Array.isArray(result)
        ? result
        : [];

      this.productLabel = '';
      this.rows = (items as ImportRow[]).map((item) => ({ ...item }));

      const domains = this.collectCompanyDomains(this.rows);
      const companies = await this.getCompanyData(domains);

      const rawVendorDomain =
        (typeof result?.ProductVendorDomainName === 'string'
          ? result.ProductVendorDomainName
          : undefined) ??
        (typeof items[0]?.ProductVendorDomainName === 'string'
          ? items[0]?.ProductVendorDomainName
          : undefined) ??
        '';

      const vendorDomainForQuery =
        this.normalizeDomain(rawVendorDomain) ||
        (typeof rawVendorDomain === 'string' ? rawVendorDomain.trim() : '');

      const rawProductName =
        (typeof result?.ProductName === 'string'
          ? result.ProductName
          : undefined) ??
        (typeof items[0]?.ProductName === 'string'
          ? items[0]?.ProductName
          : undefined) ??
        '';

      const normalizedProductName = rawProductName.trim();

      this.productsVendorDomain = vendorDomainForQuery || null;
      this.productsQueryName = normalizedProductName || null;

      const products = await this.getProductsData(
        vendorDomainForQuery,
        normalizedProductName || undefined
      );
      this.selectedProducts = products;
      this.selectedProduct = products.length > 0 ? products[0] : null;
      this.productLabel =
        this.selectedProduct?.name ||
        normalizedProductName ||
        this.productLabel ||
        '';

      this.attachCompaniesToRows(companies);
    } catch (error) {
      console.error('Failed to load product companies', error);
      this.rows = [];
      this.selectedCompanies = [];
      this.selectedProducts = [];
      this.selectedProduct = null;
      this.productsVendorDomain = null;
      this.productsQueryName = null;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private collectCompanyDomains(rows: ImportRow[]): string[] {
    const unique = new Set<string>();

    for (const row of rows) {
      const domain = this.normalizeDomain(row.CompanyDomainName);
      if (domain) {
        unique.add(domain);
      }
    }

    return Array.from(unique);
  }

  async openProductSelectDialog(): Promise<void> {
    if (!this.productsVendorDomain) {
      return;
    }

    const dialogRef = this.dialog.open(ProductSelectDialogComponent, {
      data: {
        vendorDomain: this.productsVendorDomain || '',
        productName: this.productsQueryName || undefined,
        pageSize: 10,
      },
    });

    const selected = await firstValueFrom(dialogRef.afterClosed());

    if (selected) {
      this.selectedProduct = selected;
      this.productLabel = selected.name ?? this.productLabel ?? '';

      const exists = this.selectedProducts.some(
        (product) =>
          (product.id &&
            selected.id &&
            product.id === selected.id) ||
          (product.name &&
            selected.name &&
            product.name === selected.name)
      );

      if (!exists) {
        this.selectedProducts = [selected, ...this.selectedProducts];
      }
    }

    this.cdr.markForCheck();
  }

  async importSelected(): Promise<void> {
    if (!this.canImport) {
      return;
    }

    const products = this.getTargetProducts();
    const companies = this.selectedCompanies.filter((company) => !!company?.id);

    if (!products.length || !companies.length) {
      return;
    }

    this.importing = true;
    this.cdr.markForCheck();

    const timestamp = new Date().toISOString();
    const tasks: Array<Promise<void>> = [];

    for (const company of companies) {
      const companyId = company.id;
      if (!companyId) {
        continue;
      }

      for (const product of products) {
        const productId = product?.id;
        if (!productId) {
          continue;
        }

        tasks.push(this.upsertInstall(companyId, productId, timestamp));
      }
    }

    const processing = Promise.allSettled(tasks)
      .then((results) => {
        const rejected = results.filter((item) => item.status === 'rejected');
        if (rejected.length) {
          console.error('Import failed for some company/product pairs', rejected);
        }
      })
      .catch((error) => {
        console.error('Import encountered an unexpected error', error);
      })
      .finally(() => {
        this.importing = false;
        this.cdr.markForCheck();
      });

    this.dialogRef.close({ processing });
  }

  private getTargetProducts(): ProductDto[] {
    if (this.selectedProduct) {
      return [this.selectedProduct];
    }

    if (this.selectedProducts.length === 1) {
      return [this.selectedProducts[0]];
    }

    return [];
  }

  private async getCompanyData(domains: string[]): Promise<CompanyDto[]> {
    if (!domains.length) {
      return [];
    }

    const requestBody: CompanyPageRequestDto = {
      pageNumber: 0,
      pageSize: domains.length,

      filters: {
        search: '',
        filterType: GlobalSearchResultTypeEnum.COMPANY,
        website: domains,
      },
    };

    try {
      const response = await firstValueFrom(
        this.companyService.findCompanies({ body: requestBody })
      );

      return response?.content ?? [];
    } catch (error) {
      console.error('Failed to load companies by domain', error);
      return [];
    }
  }


  private async getProductsData(
    vendorDomain?: string,
    productName?: string
  ): Promise<ProductDto[]> {
    const sanitizedVendorDomain = vendorDomain
      ? this.normalizeDomain(vendorDomain)
      : '';

    if (!sanitizedVendorDomain) {
      return [];
    }

    try {
      const response = await firstValueFrom(
        this.vendorService.getProducts({
          vendorDomain: sanitizedVendorDomain,
          productName,
          size: 10,
          page: 0,
        })
      );

      return response?.content ?? [];
    } catch (error) {
      console.error('Failed to load products by vendor', error);
      return [];
    }
  }

  private async upsertInstall(
    companyId: string,
    productId: string,
    timestamp: string
  ): Promise<void> {
    const existing = await this.fetchInstall(companyId, productId);
    const payload: TechStackInstallDto = {
      dateFirstVerified: existing?.dateFirstVerified ?? timestamp,
      dateLastVerified: timestamp,
      intensity: (existing?.intensity ?? 0) + 1,
    };

    await firstValueFrom(
      this.installService.updateInstall({
        companyId,
        productId,
        body: payload,
      })
    );
  }

  private async fetchInstall(
    companyId: string,
    productId: string
  ): Promise<TechStackInstallDto | null> {
    try {
      return await firstValueFrom(
        this.installService.getInstall({
          companyId,
          productId,
        })
      );
    } catch (error) {
      if (!this.isNotFoundError(error)) {
        console.warn('Unable to fetch install, defaulting to new payload', {
          companyId,
          productId,
          error,
        });
      }

      return null;
    }
  }

  private attachCompaniesToRows(companies: CompanyDto[]): void {
    const companyMap = new Map<string, CompanyDto>();

    for (const company of companies ?? []) {
      const domain = this.normalizeDomain(company?.website);
      if (domain && !companyMap.has(domain)) {
        companyMap.set(domain, company);
      }
    }

    this.selectedCompanies = [];

    this.rows = this.rows.map((row) => {
      const domain = this.normalizeDomain(row.CompanyDomainName);
      const selectedCompany = domain ? companyMap.get(domain) ?? null : null;
      const isSelected = Boolean(selectedCompany);

      if (isSelected && selectedCompany) {
        this.addCompanyToSelection(selectedCompany);
      }

      return {
        ...row,
        selectedCompany,
        isSelected,
      };
    });
  }

  toggleRowSelection(row: ImportRow, checked: boolean): void {
    if (!row.selectedCompany) {
      row.isSelected = false;
      return;
    }

    row.isSelected = checked;

    if (checked) {
      this.addCompanyToSelection(row.selectedCompany);
    } else {
      this.removeCompanyFromSelection(row.selectedCompany);
    }

    this.cdr.markForCheck();
  }

  private addCompanyToSelection(company: CompanyDto): void {
    const key = this.getCompanyKey(company);
    if (!key) {
      return;
    }

    const exists = this.selectedCompanies.some(
      (item) => this.getCompanyKey(item) === key
    );

    if (!exists) {
      this.selectedCompanies = [...this.selectedCompanies, company];
    }
  }

  private removeCompanyFromSelection(company: CompanyDto): void {
    const key = this.getCompanyKey(company);
    if (!key) {
      return;
    }

    this.selectedCompanies = this.selectedCompanies.filter(
      (item) => this.getCompanyKey(item) !== key
    );
  }

  private getCompanyKey(company?: CompanyDto | null): string | null {
    if (!company) {
      return null;
    }

    if (company.id) {
      return company.id;
    }

    const domain = this.normalizeDomain(company.website);
    return domain || null;
  }

  private normalizeDomain(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    const trimmed = value.trim().toLowerCase();
    if (!trimmed) {
      return '';
    }

    const withoutProtocol = trimmed.replace(/^https?:\/\//, '');
    const withoutWww = withoutProtocol.replace(/^www\./, '');

    return withoutWww.split('/')[0];
  }

  private isNotFoundError(error: unknown): boolean {
    const status = (error as { status?: number })?.status;
    return status === 404;
  }

  onClose() {
    this.dialogRef.close();
  }
}
